/** Contas arquivadas — e o caminho de volta (C5 da auditoria).
 *
 * Até 14/09 o app dizia, na própria confirmação de arquivar, que "ainda não tem tela para
 * desarquivar". Arquivar era uma porta de mão única: a conta saía do painel, o histórico
 * ficava no banco e não havia nenhum lugar no app onde ela reaparecesse. Quem arquivasse por
 * engano — ou quem voltasse a usar a conta meses depois — dependia de linha de comando.
 *
 * `GET /accounts?include_archived=true` e `PATCH /accounts/{id}` com `archived` já existiam
 * desde a F1. Faltava a tela, como no Planejar: a API entregava e nenhum caminho chegava lá.
 *
 * Fica fora de Contas de propósito: conta arquivada não é conta do dia a dia, e misturá-la
 * nos grupos do painel desfaria o efeito de ter arquivado.
 */

import { useState } from "react";
import { useNavigate } from "react-router";
import { useToast } from "../components/Toast";
import { Empty, ErrorBanner, Face, Key, Label, Skeleton } from "../components/ui";
import { money } from "../lib/format";
import { useAccountsComArquivadas, useUpdateAccount } from "../lib/queries";
import type { Account, AccountKind } from "../lib/types";

/** A tecla do sistema mede 32px; no dedo o alvo mínimo é 44. */
const ALVO = { minHeight: 44 };

const KIND_LABEL: Record<AccountKind, string> = {
  checking: "Conta corrente",
  cash: "Dinheiro",
  savings: "Reserva",
  investment: "Investimento",
  credit_card: "Cartão de crédito",
  loan: "Crediário",
};

/** `archived_at` chega como instante (com hora e fuso); na tela só o dia importa. */
function diaDe(iso: string | null): string {
  if (!iso) return "—";
  const d = iso.slice(0, 10);
  return `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
}

export function Arquivadas() {
  const nav = useNavigate();
  const contas = useAccountsComArquivadas();

  const cabeca = (
    <div className="monthbar">
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <Key icon="chevronLeft" title="Voltar" onClick={() => nav("/contas")} style={ALVO} />
        <h2>Contas arquivadas</h2>
      </div>
    </div>
  );

  if (contas.isPending)
    return (
      <section className="view on" aria-label="Contas arquivadas">
        <Skeleton lines={5} />
      </section>
    );

  if (contas.error)
    return (
      <section className="view on" aria-label="Contas arquivadas">
        <div className="plan">
          {cabeca}
          <ErrorBanner message={(contas.error as Error).message} onRetry={() => contas.refetch()} />
        </div>
      </section>
    );

  const lista = (contas.data ?? []).filter((a) => a.archived_at);

  return (
    <section className="view on" aria-label="Contas arquivadas">
      <div className="plan">
        {cabeca}

        <Face className="meter">
          <Label>O que é arquivar</Label>
          <div className="sub">
            Arquivar tira a conta do painel e guarda tudo que passou por ela. Nada foi apagado:
            desarquivar devolve a conta ao lugar de onde ela saiu, com o saldo e o extrato
            inteiros.
          </div>
        </Face>

        <Face className="log">
          <div className="sec">
            <Label>Arquivadas</Label>
            <Label dim>
              {lista.length} {lista.length === 1 ? "conta" : "contas"}
            </Label>
          </div>
          {lista.length === 0 && (
            <Empty title="Nenhuma conta arquivada">
              Quando você arquivar uma conta, ela aparece aqui — e volta daqui.
            </Empty>
          )}
          {lista.map((a) => (
            <Linha key={a.id} conta={a} />
          ))}
        </Face>
      </div>
    </section>
  );
}

function Linha({ conta }: { conta: Account }) {
  const desarquivar = useUpdateAccount();
  const aviso = useToast();
  const [erro, setErro] = useState<string | null>(null);

  const voltar = async () => {
    setErro(null);
    try {
      await desarquivar.mutateAsync({ id: conta.id, archived: false });
      aviso.ok(`${conta.name} voltou para as suas contas`);
    } catch (e) {
      setErro((e as Error).message);
    }
  };

  return (
    <>
      <div className="line">
        <div className="d">
          <i
            style={{
              display: "block",
              width: 12,
              height: 12,
              borderRadius: 2,
              background: conta.color ?? "var(--muted)",
            }}
          />
        </div>
        <div className="what">
          <div className="t">
            <span className="desc">{conta.name}</span>
          </div>
          <small>
            {KIND_LABEL[conta.kind]} · arquivada em {diaDe(conta.archived_at)}
          </small>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", paddingRight: 14 }}>
          <span className="amt" style={{ paddingRight: 0 }}>
            {money(conta.balance)}
          </span>
          <Key style={ALVO} disabled={desarquivar.isPending} onClick={voltar}>
            {desarquivar.isPending ? "Voltando…" : "Desarquivar"}
          </Key>
        </div>
      </div>
      {erro && <ErrorBanner message={erro} />}
    </>
  );
}
